import type { PermissionKey } from "@vaulted/shared";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useSession } from "../session";
import { cx } from "./ui";

interface SettingsItem {
  to: string;
  label: string;
  /** Any of these; none = everyone. */
  needs?: PermissionKey[];
}

const S: PermissionKey[] = ["settings.manage"];

/** Everything under Settings, in groups. Catalog, Staff, Roles, and the audit log keep their own addresses. */
export const SETTINGS_GROUPS: { title: string; items: SettingsItem[] }[] = [
  {
    title: "Clinic",
    items: [
      { to: "/settings/clinic", label: "Clinic", needs: S },
      { to: "/settings/locations", label: "Locations", needs: S },
      { to: "/settings/resources", label: "Resources", needs: S },
      { to: "/settings/branding", label: "Branding and security", needs: S },
      { to: "/settings/devices", label: "Devices", needs: S },
      { to: "/settings/phone", label: "Phone", needs: S },
    ],
  },
  {
    title: "Services",
    items: [
      { to: "/catalog", label: "Catalog" },
      { to: "/settings/tags", label: "Tags", needs: S },
      { to: "/settings/booking", label: "Booking and payments", needs: S },
      { to: "/settings/mobile", label: "Mobile", needs: S },
      { to: "/settings/gfe", label: "Good faith exams", needs: S },
    ],
  },
  {
    title: "Forms and messages",
    items: [
      { to: "/settings/forms", label: "Forms and charts", needs: S },
      { to: "/settings/messages", label: "Messages", needs: S },
      { to: "/settings/marketing", label: "Marketing tracking", needs: S },
      { to: "/settings/announcements", label: "Announcements", needs: S },
    ],
  },
  {
    title: "Team",
    items: [
      { to: "/staff", label: "Staff", needs: ["staff.manage"] },
      { to: "/roles", label: "Roles and permissions", needs: ["roles.manage"] },
    ],
  },
  {
    title: "Records",
    items: [
      { to: "/audit", label: "Audit log", needs: ["audit.view"] },
      { to: "/settings/setup", label: "Export / import setup", needs: S },
      { to: "/settings/boulevard", label: "Boulevard (read-only)", needs: S },
    ],
  },
];

/** The Settings pages this person can open, by group (empty groups left out). */
export function useSettingsGroups() {
  const { can } = useSession();
  return SETTINGS_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !i.needs || can(...i.needs)) })).filter((g) => g.items.length);
}

/** Is this path one of the Settings pages? (For the side menu.) */
export const isSettingsPath = (path: string) => SETTINGS_GROUPS.some((g) => g.items.some((i) => path === i.to || path.startsWith(`${i.to}/`))) || path.startsWith("/settings");

/** Settings: a grouped list of its pages beside the page itself (a dropdown on a phone). */
export function SettingsLayout() {
  const groups = useSettingsGroups();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const all = groups.flatMap((g) => g.items);
  const current = all.find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
  return (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
      <label className="flex flex-col gap-1 lg:hidden">
        <span className="text-xs font-extrabold uppercase tracking-[.08em] text-muted">Settings</span>
        <select className="rounded-[var(--radius-control)] border border-line bg-white px-3 py-2" value={current?.to ?? ""} onChange={(e) => nav(e.target.value)}>
          {!current && <option value="">Choose…</option>}
          {groups.map((g) => (
            <optgroup key={g.title} label={g.title}>
              {g.items.map((i) => (
                <option key={i.to} value={i.to}>
                  {i.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <nav aria-label="Settings" className="sticky top-6 hidden w-52 shrink-0 flex-col gap-4 lg:flex">
        <div className="font-display text-2xl">Settings</div>
        {groups.map((g) => (
          <div key={g.title} className="flex flex-col gap-0.5">
            <div className="px-3 pb-1 text-xs font-extrabold uppercase tracking-[.08em] text-muted">{g.title}</div>
            {g.items.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                className={({ isActive }) => cx("rounded-[var(--radius-control)] px-3 py-1.5 text-[15px]", isActive ? "bg-white font-semibold text-ink shadow-sm ring-1 ring-line" : "text-ink-soft hover:bg-mist")}
              >
                {i.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
